// SESSION-TIMELINE (A19): the strip's read and its one write. The read is the timeline route; the
// write is the ordinary comment routes: `messages.create` on a card's thread (`tasks.thread`) or
// on the session thread, so the viewer's rights on the card decide whether it lands.
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../../api/client";
import {
  type FeedbackTarget,
  feedbackClientId,
  feedbackMarkdown,
  type Snapshot,
  timelineKeys,
} from "./timeline-model";

export const timelineQuery = (api: Api, co: string, session: string) =>
  queryOptions({
    queryKey: timelineKeys.session(co, session),
    queryFn: () =>
      api.client.liveCanvas.timeline({ params: { co }, query: { session_id: session } }),
    meta: { quietStatuses: [403, 404] },
  });

/** The thread a target's comment goes to. */
export async function threadOfTarget(api: Api, co: string, t: FeedbackTarget): Promise<string> {
  if (t.kind === "thread") return t.id;
  return (await api.client.tasks.thread({ params: { co, id: t.id } })).thread_id;
}

/** Feedback on a snapshot as a comment on its card (or the session thread). Returns the thread. */
export async function sendFeedback(
  api: Api,
  co: string,
  session: string,
  snapshot: Snapshot,
  text: string,
): Promise<{ threadId: string; messageId: string }> {
  if (!snapshot.target)
    throw new Error("This snapshot has no card or session thread to comment on.");
  const threadId = await threadOfTarget(api, co, snapshot.target);
  const posted = await api.client.messages.create({
    params: { co, id: threadId },
    body: {
      client_id: feedbackClientId(session, snapshot, text),
      body_markdown: feedbackMarkdown(snapshot, text),
    },
  });
  return { threadId, messageId: posted.id };
}
